import { createBlockEvent } from '@/views/editor/composable/useNote.ts'
import { renderLineChartSVG } from '@/composables/chart/useLineChart.ts'
import { renderBarChartSVG } from '@/composables/chart/useBarChart.ts'
import { renderRowBarChartSVG } from '@/composables/chart/useRowBarChart.ts'
import { renderDonutChart } from '@/composables/chart/useDonutChart.ts'
import { renderPieChart } from '@/composables/chart/usePieChart.ts'
import { renderScatterChart } from '@/composables/chart/useScatterChart.ts'
import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'
import { useCommonStore } from '@/stores/common.ts'
import { nextTick } from 'vue'

export function useChart() {
	function init() {
		const { chartData } = storeToRefs(useEditorStore())

		chartData.value.forEach((item: any) => {
			const chartDataList = typeof item.chartDataList === 'string' ? JSON.parse(item.chartDataList) : item.chartDataList
			createChartSvg(chartDataList)
		})
	}

	async function createChartSvg(data: any) {
		if (!data?.chartTargetUid) return

		let doc = null
		let chartTargetUid = `#${data.chartTargetUid}`
		if (data.chartTargetUid.includes('temp-')) {
			doc = document.getElementById(data.chartTargetUid) as HTMLElement
		} else {
			chartTargetUid = `#prefix-${data.chartTargetUid}`
			doc = document.getElementById(`prefix-${data.chartTargetUid}`) as HTMLElement
		}

		if (doc) {
			await nextTick()

			if (!doc.querySelector('svg')) {
				let series: any = []

				switch (data.chartType) {
					case 'vertical':
						data.chartData.forEach((item: any, index: number) => {
							series.push({
								name: data.labelName[index] || `label ${index}`,
								color: data.colorList[index],
								data: item,
							})
						})
						renderBarChartSVG(chartTargetUid, {
							title: data.chartName,
							xLabel: data.xLabel,
							yLabel: data.yLabel,
							xCategories: data.dataName,
							series: series,
							showArea: true,
						})
						break

					case 'line':
						data.chartData.forEach((item: any, index: number) => {
							series.push({
								name: data.labelName[index] || `label ${index}`,
								color: data.colorList[index],
								data: item,
							})
						})
						renderLineChartSVG(chartTargetUid, {
							title: data.chartName,
							xLabel: data.xLabel,
							yLabel: data.yLabel,
							xCategories: data.dataName,
							series: series,
							showArea: true,
							curve: 'smooth',
							smoothness: 0.8,
						})
						break

					case 'bar':
						data.chartData.forEach((item: any, index: number) => {
							series.push({
								name: data.labelName[index] || `label ${index}`,
								color: data.colorList[index],
								data: item,
							})
						})

						renderRowBarChartSVG(chartTargetUid, {
							title: data.chartName,
							xLabel: data.xLabel,
							yLabel: data.yLabel,
							xCategories: data.dataName,
							series: series,
							showArea: true,
						})
						break

					case 'pie':
						data.chartData.forEach((item: any, index: number) => {
							series.push({
								label: data.labelName[index] || `label ${index}`,
								color: data.colorList[index],
								data: item,
							})
						})
						renderPieChart(chartTargetUid, {
							title: data.chartName,
							data: series,
							innerRadius: 0,
							ringWidth: 100,
							gapDeg: 0,
							startAngle: -120,
							// 뒤쪽 stripe의 비율 기준(없으면 데이터에서 최대 자동)
							// maxStripeValue: 40,
							stripeWidth: 10,
							stripeGap: 4,
						})

						break

					case 'donut':
						data.chartData.forEach((item: any, index: number) => {
							series.push({
								label: data.labelName[index] || `label ${index}`,
								color: data.colorList[index],
								data: item,
							})
						})
						renderDonutChart(chartTargetUid, {
							title: data.chartName,
							data: series,
							innerRadius: 48,
							ringWidth: 56,
							gapDeg: 2,
							startAngle: -120,
							// 뒤쪽 stripe의 비율 기준(없으면 데이터에서 최대 자동)
							// maxStripeValue: 40,
							stripeWidth: 10,
							stripeGap: 4,
						})
						break

					case 'scatter':
						series = (data.chartData || []).map((row: any[], idx: number) => ({
							name: data.dataName?.[idx] ?? `시리즈${idx + 1}`,
							color: data.colorList?.[idx] ?? '#22C1D6',
							// 숫자 배열이라면 x는 인덱스, y는 값으로 매핑
							data: row.map((y: any, xIdx: number) => ({
								x: xIdx + 1,
								y: Number(y) || 0,
							})),
						}))
						renderScatterChart(chartTargetUid, {
							title: data.chartName,
							xLabel: data.xLabel,
							yLabel: data.yLabel,
							series,
							// 뒤쪽 stripe의 비율 기준(없으면 데이터에서 최대 자동)
							// maxStripeValue: 40,
						})
						break
				}
			}
		}
		/*
		renderScatterChart('#chart', {
			title: 'Line chart name',
			xLabel: '(x축 레이블)',
			yLabel: '(y축 레이블)',
			series: [
				{
					name: '라벨1번',
					color: '#9CA3AF',
					data: [
						{ x: 10, y: 100 },
						{ x: 20, y: 180 },
						{ x: 30, y: 120 },
						{ x: 40, y: 200 },
						{ x: 50, y: 160 },
					],
				},
				{
					name: '라벨2번',
					color: '#22C1D6',
					data: [
						{ x: 5, y: 20 },
						{ x: 15, y: 80 },
						{ x: 25, y: 55 },
						{ x: 35, y: 95 },
						{ x: 45, y: 70 },
					],
				},
				{
					name: '라벨3번',
					color: '#F4C542',
					data: [
						{ x: 12, y: 140 },
						{ x: 22, y: 100 },
						{ x: 32, y: 180 },
						{ x: 42, y: 160 },
						{ x: 52, y: 120 },
					],
				},
				{
					name: '라벨4번',
					color: '#A78BFA',
					data: [
						{ x: 8, y: 90 },
						{ x: 18, y: 150 },
						{ x: 28, y: 110 },
						{ x: 38, y: 170 },
						{ x: 48, y: 130 },
					],
				},
				{
					name: '라벨5번',
					color: '#1AC54F',
					data: [
						{ x: 7, y: 60 },
						{ x: 17, y: 140 },
						{ x: 27, y: 100 },
						{ x: 37, y: 180 },
						{ x: 47, y: 120 },
					],
				},
			],
			showArea: true,
		})
		*/
	}

	function updateChart() {
		const { viewBlockList } = storeToRefs(useEditorStore())

		viewBlockList.value.forEach((item: any) => {
			if (item.type === 'chart') {
				let doc: any
				if (item.blockUid.includes('temp-')) {
					doc = document.getElementById(item.blockUid) as HTMLElement
				} else {
					doc = document.getElementById(`prefix-${item.blockUid}`) as HTMLElement
				}

				if (doc.querySelector('svg')) {
					const svg = doc.querySelector('svg') as HTMLElement
					svg.remove()
				}
			}
		})
	}

	async function createChart() {
		await createBlockEvent('chart')
		await nextTick()
	}

	function setChartData() {
		const { socket } = storeToRefs(useCommonStore())
		const { noteUid, chartDataList } = storeToRefs(useEditorStore())
		socket.value.modifyNoteBlock(noteUid.value, chartDataList.value.chartTargetUid, JSON.stringify(chartDataList.value), 'chart')
	}

	return {
		init,
		createChart,
		setChartData,
		updateChart,
		createChartSvg,
	}
}
